"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";
import isPermitted from "@/utils/isPermitted";
import { useTranslation } from "react-i18next";

interface UserProfileDropdownProps {
  userName: string;
  userEmail: string;
  avatarSrc: string;
}

export default function UserProfileDropdown({
  userName,
  userEmail,
  // avatarSrc,
}: UserProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout, isLTISession, user, allPermissions } = useAuth();
  const { t } = useTranslation("common");
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex items-center space-x-3 focus:outline-none"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-sm font-medium text-gray-900 tracking-normal leading-6 hidden md:block">
          {userName}
        </span>
        {/* <Image
          src={avatarSrc}
          alt="User avatar"
          width={36}
          height={36}
          className="rounded-full"
        /> */}
        {userName && (
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-sm font-medium text-gray-600">
              {userName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </button>

      {isOpen && user && (
        <div className="absolute right-0 mt-2 w-max bg-white rounded border border-gray-200 shadow-lg z-[60]">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col text-center justify-center">
              <span className="text-[10px] leading-[12px] tracking-normal font-semibold text-gray-900">
                {userEmail}
              </span>
              {/* <Image
                src={avatarSrc}
                alt="User avatar"
                width={48}
                height={48}
                className="rounded-full mt-3 mx-auto"
              /> */}
              {userName && (
                <div className="w-9 h-9 rounded-full mx-auto mt-3 bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-sm font-medium leading-[24px] tracking-normal text-gray-900">
                {userName}
              </span>
            </div>
          </div>
          {isLTISession == false && (
            <div className="py-5 px-4 flex flex-col gap-5">
              {isPermitted(
                user,
                PAGE_ROUTES.SIDEBAR_PERMISSIONS.ACCOUNT_SETTINGS,
                allPermissions
              ) && (
                <Link
                  href="/account-settings"
                  className="flex items-center gap-3 font-medium text-[14px] leading-[21px] tracking-normal hover:opacity-70 transition-all ease-in-out duration-200"
                  onClick={() => setIsOpen(false)}
                >
                  <Image
                    src="/universal/user-icon.svg"
                    width={18}
                    height={18}
                    alt="user icon"
                  />
                  {t("userProfileDropdown.account")}
                </Link>
              )}

              <button
                className="flex cursor-pointer items-center gap-3 font-medium text-[14px] leading-[21px] tracking-normal hover:opacity-70 transition-all ease-in-out duration-200"
                onClick={() => {
                  logout();
                  setIsOpen(false);
                }}
              >
                <Image
                  src="/universal/logout-icon.svg"
                  width={18}
                  height={18}
                  alt="logout"
                />
                {t("userProfileDropdown.logout")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
